import {
  confirmUserAttribute,
  fetchUserAttributes,
  confirmResetPassword,
  updatePassword,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  sendUserAttributeVerificationCode,
  signIn,
  confirmSignIn,
} from "@aws-amplify/auth";
import { authControllerEnsureUser } from "@/lib/lutely-api/gen/auth/auth";
import {
  consumePendingEmailVerification,
  emailsMatch,
} from "@/lib/storage-utils";
import type { GetCurrentUserOutput } from "@aws-amplify/auth";

export async function sendUserEmailVerification(): Promise<void> {
  await sendUserAttributeVerificationCode({
    userAttributeKey: "email",
  });
}

export async function verifyUserEmailAttribute(code: string): Promise<void> {
  await confirmUserAttribute({
    userAttributeKey: "email",
    confirmationCode: code,
  });
}

// Replays a code stashed by the logged-out link flow, but only when it belongs
// to the account that just signed in. Cognito applies a verification code to
// whichever session presents it, so replaying one blindly verifies the wrong
// account.
export function pendingEmailVerificationRedirect(
  authedEmail: string | null | undefined,
): string | null {
  const pending = consumePendingEmailVerification();
  if (!pending) return null;

  // Older stashes carry no address, so there's no way to prove they belong
  // here. Dropping one costs a click; misapplying it is the bug.
  if (!emailsMatch(pending.email, authedEmail)) return null;

  return `/auth/verify-email?code=${encodeURIComponent(
    pending.code,
  )}&email=${encodeURIComponent(pending.email!)}`;
}

export async function isUserEmailVerified(): Promise<boolean> {
  try {
    const attributes = await fetchUserAttributes();
    const verifiedStatus = attributes.email_verified;

    return verifiedStatus === "true";
  } catch (error) {
    console.error(
      "Error fetching user attributes for verification check:",
      error,
    );
    return false;
  }
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword: newPassword,
  });
}

// Note: This doesn't work if a user was added directly in the AWS console and a temporary generated password was created
// Fix is adjusting this method to authenticate the user first and then change the password
export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  await updatePassword({
    oldPassword,
    newPassword,
  });
}

export async function getCurrentUser(): Promise<GetCurrentUserOutput> {
  return amplifyGetCurrentUser();
}

export async function hasActiveSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();

    return session.tokens !== undefined;
  } catch {
    return false;
  }
}

export async function initiateOtcAuth(
  email: string,
): Promise<{ success: boolean }> {
  await authControllerEnsureUser({ email });

  const { nextStep } = await signIn({
    username: email,
    options: {
      authFlowType: "CUSTOM_WITHOUT_SRP",
    },
  });

  if (nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
    throw new Error(`Unexpected sign-in step: ${nextStep.signInStep}`);
  }

  return { success: true };
}

export async function verifyOtcCode(
  code: string,
): Promise<{ accessToken: string; idToken: string }> {
  await confirmSignIn({ challengeResponse: code });

  const { tokens } = await fetchAuthSession({ forceRefresh: true });

  if (!tokens?.accessToken || !tokens?.idToken) {
    throw new Error("Failed to retrieve tokens after code verification");
  }

  return {
    accessToken: tokens.accessToken.toString(),
    idToken: tokens.idToken.toString(),
  };
}

export function decodeOAuthProvider(
  state: string,
): "google" | "apple" | "unknown" {
  const encoded = state.split("-").pop();

  if (!encoded) return "unknown";

  try {
    const decoded = decodeURIComponent(encoded.replace(/(..)/g, "%$1"));

    if (decoded === "google" || decoded === "apple") {
      return decoded;
    }
  } catch {
    // ignore
  }

  return "unknown";
}
